import { useCallback, useEffect, useRef, useState } from 'react'

import type { WorkflowStep } from './useIncidentWorkflow'

import { useToast } from '@chakra-ui/react'

import {
  IncidentWorkflowActionsDocument,
  useCreateWorkflowActionMutation,
} from '@/graphql/generated/hooks'
import { BUTTON_PRESS, trackEvent } from '@/utils/analytics'

const EDITABLE_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT'])
const NUMBER_HOTKEY_PATTERN = /^[1-9]$/
const WORKFLOW_STEP_NAVIGATE_EVENT = `${BUTTON_PRESS} Workflow Step - Navigate`
const WORKFLOW_STEP_SUBMIT_EVENT = `${BUTTON_PRESS} Workflow Step - Submit`

type WorkflowNavigationDirection = 'up' | 'down'
type WorkflowSubmitMethod = 'enter_key' | 'digit_key' | 'click'

interface UseWorkflowInProgressIProps {
  currentStep: WorkflowStep | null
  incidentId: string | null
  isActiveOwner: boolean
}

interface UseWorkflowInProgressResultI {
  selectedIndex: number
  isSubmitting: boolean
  updateSelectedIndex: (idx: number) => void
  submitSelectedAnswer: (submitMethod: WorkflowSubmitMethod) => void
}

export const useWorkflowInProgress = ({
  currentStep,
  incidentId,
  isActiveOwner,
}: UseWorkflowInProgressIProps): UseWorkflowInProgressResultI => {
  const toast = useToast()

  // selectedIndex tracks which edge is selected (as a number index, always unique).
  // selectedIndexRef mirrors the state synchronously so the global keydown handler
  // always reads the latest value even before the async state update settles.
  const [selectedIndex, setSelectedIndex] = useState(0)
  const selectedIndexRef = useRef(0)

  // Synchronous in-flight lock to prevent duplicate submissions from rapid
  // double-clicks. Apollo's isSubmitting flag only updates after a re-render,
  // so a second click can slip through before the flag is set.
  const isSubmittingRef = useRef(false)

  const updateSelectedIndex = (idx: number) => {
    selectedIndexRef.current = idx
    setSelectedIndex(idx)
  }

  // Reset to first option whenever the step changes
  useEffect(() => {
    updateSelectedIndex(0)
  }, [currentStep?.id])

  const [createWorkflowAction, { loading: isSubmitting }] =
    useCreateWorkflowActionMutation()

  const trackAnswerNavigation = useCallback(
    (direction: WorkflowNavigationDirection, answerIndex: number) => {
      if (!incidentId || !currentStep) return

      trackEvent(WORKFLOW_STEP_NAVIGATE_EVENT, {
        direction,
        answer_index: answerIndex,
        step_id: currentStep.id,
        incident_id: incidentId,
      })
    },
    [incidentId, currentStep]
  )

  const submitSelectedAnswer = useCallback(
    (submitMethod: WorkflowSubmitMethod) => {
      if (
        !isActiveOwner ||
        !incidentId ||
        !currentStep ||
        isSubmitting ||
        isSubmittingRef.current
      )
        return

      // Use the ref so we always submit the latest selected index even if the
      // async state update hasn't flushed yet (race with QuestionCard's onSelectAnswer).
      const answerIndex = selectedIndexRef.current
      const edge = currentStep.edges[answerIndex]
      if (!edge) return

      isSubmittingRef.current = true
      createWorkflowAction({
        variables: {
          input: {
            edgeLabel: edge.label,
            incidentId,
            nodeId: currentStep.id,
          },
        },
        awaitRefetchQueries: true,
        refetchQueries: [IncidentWorkflowActionsDocument],
        update: (cache, { data }) => {
          const incident = data?.createWorkflowAction
          if (!incident?.id) return

          const cacheId = cache.identify({
            __typename: 'Incident',
            id: incident.id,
          })
          if (!cacheId) return

          cache.modify({
            fields: {
              currentWorkflowStep: () => incident.currentWorkflowStep,
              workflowEndTime: () => incident.workflowEndTime,
              workflowId: () => incident.workflowId,
              workflowStatus: () => incident.workflowStatus,
            },
            id: cacheId,
          })
        },
        onCompleted: () => {
          isSubmittingRef.current = false
          trackEvent(WORKFLOW_STEP_SUBMIT_EVENT, {
            submit_method: submitMethod,
            answer_index: answerIndex,
            step_id: currentStep.id,
            incident_id: incidentId,
          })
        },
        onError: () => {
          isSubmittingRef.current = false
          toast({
            title: 'Failed to submit workflow action',
            status: 'error',
            duration: 4000,
            isClosable: true,
          })
        },
      })
    },
    [
      isActiveOwner,
      incidentId,
      currentStep,
      isSubmitting,
      createWorkflowAction,
      toast,
    ]
  )

  useEffect(() => {
    const answerOptions = currentStep?.edges ?? []

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isActiveOwner) return
      if (!answerOptions.length) return

      const target = event.target as HTMLElement
      if (EDITABLE_TAGS.has(target.tagName) || target.isContentEditable) return

      // Number response hotkeys intentionally support single-digit options 1-9.
      if (NUMBER_HOTKEY_PATTERN.test(event.key)) {
        if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey)
          return

        const nextIndex = Number(event.key) - 1
        if (!answerOptions[nextIndex]) return

        event.preventDefault()
        updateSelectedIndex(nextIndex)
        submitSelectedAnswer('digit_key')
        return
      }

      if (event.key === 'Enter') {
        event.preventDefault()
        submitSelectedAnswer('enter_key')
        return
      }

      if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return

      event.preventDefault()

      const lastIndex = answerOptions.length - 1
      const direction = event.key === 'ArrowDown' ? 'down' : 'up'
      const nextIndex =
        direction === 'down'
          ? Math.min(selectedIndex + 1, lastIndex)
          : Math.max(selectedIndex - 1, 0)

      trackAnswerNavigation(direction, nextIndex)
      updateSelectedIndex(nextIndex)
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [
    isActiveOwner,
    currentStep?.edges,
    selectedIndex,
    submitSelectedAnswer,
    trackAnswerNavigation,
  ])

  return {
    selectedIndex,
    isSubmitting,
    updateSelectedIndex,
    submitSelectedAnswer,
  }
}
